import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import {  useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const TaskContext = createContext();
 
export const useTasks = () => {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error('useTasks must be used within a TaskProvider');
  }
  return context;
};

export const TaskProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [ setApi] = useState(null);
  const { handleApiError  } = useError();
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/tasks');

  const getTasks = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
        
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
      if(filters?.completed){
        query += '&completed='+filters?.completed;
      } else{
        const completed = searchParams.get('completed') || '';
        query += '&completed='+completed;
      }
      let response = await apiInstance.call('tasks?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        if(filters?.active){
          query += '&active='+filters?.active;
        } else{
          const active = searchParams.get('active') || '';
          query += '&active='+active;
        }
        if(filters?.completed){
          query += '&completed='+filters?.completed;
        } else{
          const completed = searchParams.get('completed') || '';
          query += '&completed='+completed;
        }

        response = await apiInstance.call('tasks?page=' + 0 +''+query);  
      }
      
      if (response.status === 200) {
        setPaginate(response.data.paginate)
        setTasks(response.data.tasks || []);
       
        
        if(filters){
          return response.data.tasks;
        }
      } else {
        
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch tasks information'
        });
      }
    } catch (error) {
     
    } finally {
      
    }
  }, [handleApiError, paginate, getToken]);

  const getTaskByIdApi = useCallback(async (id, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`tasks/${id}`, 'GET', null, true, false);
   
    if (response.status === 200) {
      
      let tasks = {'data':[response.data.task]}
      setTasks(tasks || []);
      
    } 
  }, [getToken]);

  useEffect(() => {
    
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/tasks/edit/'+id))){
        if (getToken()) {
        
          
          await getTaskByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getTaskByIdApi, searchParams,setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
     
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
         
          await getTasks(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getTasks, searchParams, initialized,hideLoading,setApi]);

  const getTaskAddtionalData = useCallback(async () => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call('get-tasks-additional-data', 'GET', null, true);
    if(response.status === 200){
       return response.data;
    } else{
      return handleApiError(response.error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to get task data'
      });
    } 
  }, [getToken, handleApiError]);

  const addTask = async (data) => {
    setIsLoading(true);
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      setIsLoading(false);
      return;
    }

    try {

      let formData = new FormData();
      
      // Remove any file fields from the original object so we can serialize the rest
      const dataCopy = { ...data };
      
      // Append the data as JSON
      formData.append('data', JSON.stringify(dataCopy));

      const response = await apiInstance.call('tasks', 'POST', formData, true,true);
      
      if (response.status === 201 || response.status === 200) {
        // Refresh the tasks list
        await getTasks();
        setIsLoading(false);
        return { status: response.status, data: response.data };
      } else {
        setIsLoading(false);
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Task'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      setIsLoading(false);
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create task'
      });
      return { status: 500, error: error.message };
    }
  };

  const getTask = (id) => {
    return tasks?.data?.find(task => Number(task.id) === Number(id));
  };

  const getTaskById = useCallback(async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`tasks/${id}`, 'GET', null, true);
      
      if (response.status === 200) {
        return { status: response.status, data: response.data.task };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to fetch task details'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch task details'
      });
      return { status: 500, error: error.message };
    }
  }, [getToken, handleApiError]);

  const updateTask = async (id, updatedData) => {
    setIsLoading(true);
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      setIsLoading(false);
      return;
    }

    try {
      let formData = new FormData();
      
      // Remove any file fields from the original object so we can serialize the rest
      const dataCopy = { ...updatedData };
      
      // Append the data as JSON
      formData.append('data', JSON.stringify(dataCopy));
      formData.append('_method', 'PUT');

      const response = await apiInstance.call(`tasks/${id}`, 'POST', formData, true, true);
      
      if (response.status === 200) {
        // Refresh the tasks list
        await getTasks();
        setIsLoading(false);
        return { status: response.status, data: response.data };
      } else {
        setIsLoading(false);
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update task'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      setIsLoading(false);
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update task'
      });
      return { status: 500, error: error.message };
    }
  };

  const deleteTask = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`tasks/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the tasks list
        await getTasks();
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete task'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete task'
      });
      return { status: 500, error: error.message };
    }
  };

  const value = {
    tasks,
    getTasks,
    getTaskById,
    getTask,
    addTask,
    createTask: addTask,
    updateTask,
    deleteTask,
    getTaskAddtionalData,
    isLoading
  };

  return (
    <TaskContext.Provider value={value}>
      {children}
    </TaskContext.Provider>
  );
};

export default TaskContext; 